import { createApi } from '@reduxjs/toolkit/query/react';
import { newBackendAxiosBaseQuery } from '../../utils/axiosBaseQuery';
import { serializeQueryArgsWithTenant } from '../tenantCacheKey';

/**
 * Payload for replacing all price scales on a price category. Used by the
 * asset detail (apartments: single nightly scale; venues: weekday + weekend
 * scales) and, since phase 3 of the service-configuration plan, by the
 * Services tariff editor (weekday scope + time window + duration).
 *
 * `startTime`/`endTime` are sent as 'HH:MM:SS' LOCAL wall-clock at the station
 * (ADR-0019) — the frame the column holds and both matchers compare in. The
 * editor normalizes the operator's input with `localTimeOfDayToStored` before
 * sending, mirroring how `formatStoredTimeOfDay` displays the bounds.
 */
export interface UpdatePriceScalesRequest {
    priceCategoryId: string;
    pricescales: Array<{
        durationInMinutes: number;
        price: number;
        additionalDaysPrice?: number | null;
        days?: string | null;
        isFullDay?: boolean;
        startTime?: string | null;
        endTime?: string | null;
        /** #1808 — part of the row's SCOPE: the backend matches on it and
         *  archives unmatched rows, so omitting it on an existing class row
         *  deletes that rate. `toPriceScalePayload` has sent it all along;
         *  the type just failed to say so. */
        customerClassId?: string | null;
    }>;
}

// NOTE: This slice does not declare or invalidate its own tags. The consumer
// (AssetDetailPage.handleSave) calls updatePriceScales immediately followed by
// updateAssetDetails, which invalidates assetDetails in assetApiSlice and
// triggers a refetch that observes the new price scales.
export const priceCategoryApiSlice = createApi({
    reducerPath: 'priceCategoryApi',
    serializeQueryArgs: serializeQueryArgsWithTenant,
    baseQuery: newBackendAxiosBaseQuery(),
    endpoints: (builder) => ({
        updatePriceScales: builder.mutation<void, UpdatePriceScalesRequest>({
            query: ({ priceCategoryId, pricescales }) => ({
                url: `/pricecategory/${priceCategoryId}/pricescales`,
                method: 'PUT',
                data: { pricescales },
            }),
        }),
    }),
});

export const { useUpdatePriceScalesMutation } = priceCategoryApiSlice;
